import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { format, subMonths, startOfMonth } from "date-fns";
import { useAgencyBookings, useAgencyMembers } from "@/hooks/platform/useAgency";
import { LoadingState } from "@/components/dashboard/common/PageShell";

const AnalyticsReports = () => {
  const { data: members = [], isLoading } = useAgencyMembers();
  const { data: bookings = [] } = useAgencyBookings();

  const creators = members.filter((m) => m.user_type === "creator");
  const verified = creators.filter((m) => m.verification_status === "verified").length;
  const revenue = bookings.reduce((s, b) => s + Number(b.amount ?? 0), 0);

  const monthly = useMemo(() => {
    const months = Array.from({ length: 6 }, (_, i) => startOfMonth(subMonths(new Date(), 5 - i)));
    return months.map((m) => ({
      month: format(m, "MMM"),
      bookings: bookings.filter((b) => startOfMonth(new Date(b.event_date)).getTime() === m.getTime()).length,
      value: bookings
        .filter((b) => startOfMonth(new Date(b.event_date)).getTime() === m.getTime())
        .reduce((s, b) => s + Number(b.amount ?? 0), 0),
    }));
  }, [bookings]);

  if (isLoading) return <LoadingState label="Building reports…" />;

  const stats = [
    { label: "Creators", value: creators.length.toString() },
    { label: "Verified", value: verified.toString() },
    { label: "Bookings", value: bookings.length.toString() },
    { label: "Booking value", value: `R${revenue.toFixed(0)}` },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="bg-white/5 border-white/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">{s.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-white">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white/5 border-white/20">
        <CardHeader>
          <CardTitle className="text-white text-base">Bookings by month</CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" />
              <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", color: "#fff" }} />
              <Bar dataKey="bookings" fill="#ffffff" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsReports;
